// ─────────────────────────────────────────────────────────────────────────────
// 🔑 THE BORROWER'S FIVE STEPS AFTER THE APPLICATION (Jesse/Hannah, 2026-11-02 — the Home Shopping page redesign).
// Replaces the 8-step tracker (AppShell ProgressBar) on every borrower page: "Step X of 5", the step name and the
// 5-segment bar. DISPLAY ONLY — each step groups stages that already exist (backend constants/stages.js); no stage is
// added or renamed, and nothing changes for DoorlyPro, Eva or notifications.
// Pure — no imports — so backend/test/journeySteps.test.js runs it.
//
//   1 Find your home   home_shopping; approved / conditional_approval (decided, not yet signed); not_moving_forward
//   2 Your offer       offer_requested, offer_submitted
//   3 Under contract   offer_accepted, option_period, under_contract (new construction)
//   4 Closing          loan_docs, closing_disclosures, final_review, closing, clear_to_close
//   5 Home is yours    closed, funded
//   no header          every stage before the decision (the guided application has its own steps), on_hold, adverse,
//                      adverse_pending; an archived, adversed or under-review file
// The four stages the old tracker had no slot for (conditional_approval, offer_submitted, clear_to_close, funded) are
// covered.
// ─────────────────────────────────────────────────────────────────────────────

export const JOURNEY_STEP_NAMES = ['Find your home', 'Your offer', 'Under contract', 'Closing', 'Home is yours'];
export const JOURNEY_TOTAL = 5;

export const JOURNEY_STAGE_STEP = {
  home_shopping: 1, approved: 1, conditional_approval: 1, not_moving_forward: 1,
  offer_requested: 2, offer_submitted: 2,
  offer_accepted: 3, option_period: 3, under_contract: 3,
  loan_docs: 4, closing_disclosures: 4, final_review: 4, closing: 4, clear_to_close: 4,
  closed: 5, funded: 5,
};

/** { step, name, total } for a /api/borrower/status answer, or null (no header). */
export function journeyStepFor(status) {
  if (!status || !status.loan_stage) return null;
  if (status.under_review === true || status.is_adversed || status.is_archived) return null;
  const step = JOURNEY_STAGE_STEP[String(status.loan_stage)];
  if (!step) return null;
  return { step, name: JOURNEY_STEP_NAMES[step - 1], total: JOURNEY_TOTAL };
}

// ── The main card's wording (approved, curly apostrophes) ──
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const ymdParts = (ymd) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(ymd || ''));
  return m ? { y: +m[1], mo: +m[2], d: +m[3] } : null;
};
/** 'YYYY-MM-DD' → "Nov 9" (the calendar date as stored, never shifted by a zone). */
export function formatMonD(ymd) {
  const p = ymdParts(ymd);
  return p ? `${MONTHS[p.mo - 1]} ${p.d}` : '';
}
/** 'YYYY-MM-DD' → "Monday, Dec 7". */
export function formatWeekdayMonD(ymd) {
  const p = ymdParts(ymd);
  if (!p) return '';
  return `${WEEKDAYS[new Date(Date.UTC(p.y, p.mo - 1, p.d)).getUTCDay()]}, ${MONTHS[p.mo - 1]} ${p.d}`;
}
export const formatUsd = (n) => (Number(n) > 0 ? `$${Math.round(Number(n)).toLocaleString('en-US')}` : '');

export const JOURNEY_COPY = {
  stepLine: (n) => `Step ${n} of ${JOURNEY_TOTAL}`,
  welcome: (first) => (first ? `Welcome back, ${first}` : 'Welcome back'),
  approved: (amount) => `You’re approved up to ${formatUsd(amount)}`,
  browse: 'Browse homes',
  viewSmartEstimate: 'View your SmartEstimate',
  // Today's next-step sentence and item 10's approved sentence (Hannah's review, 2026-11-02; "down payment" plain text).
  homeShoppingNext: 'Browse and find the right home. Once you request an offer, your Doorly team takes it from there.',
  downPaymentReady: 'When you’re ready to make an offer, have your down payment ready. Doorly buys the home with cash first, so we need your down payment confirmed before we commit funds to the purchase.',
  underContract: (address, price) => ['Under contract', address, formatUsd(price)].filter(Boolean).join(' · '),
  optionEnds: (ymd) => `Your option period ends ${formatMonD(ymd)} at 5:00 p.m.`,
  closingTitle: (address, price) => ['Closing', address, formatUsd(price)].filter(Boolean).join(' · '),
  closingOn: (ymd) => `Your closing is on ${formatWeekdayMonD(ymd)}.`,
  questions: 'Chat with us anytime, or visit the Help Center.',
};

/** Show the option-period line? During the option period, until 5:00 p.m. on its end date (the server's instant). */
export function showOptionEnds(status, now = Date.now()) {
  const j = status && status.journey;
  if (!status || status.loan_stage !== 'option_period' || !j || !j.option_period_end) return false;
  if (!j.option_period_ends_at) return true;
  return new Date(j.option_period_ends_at).getTime() > now;
}

// ── 🔑 THE COLOUR RULE FOR CHECKLISTS (Hannah, 2026-11-02): white; light blue for the ONE item waiting on the borrower;
// a green check for done; red ONLY for overdue. No lime, no amber, no red tints otherwise. Primary buttons are the app's
// primary (Doorly blue, white text) — never lime.
export const CHECK_COLOURS = Object.freeze({
  card: { background: '#fff', border: '1px solid #e5e7eb' },
  waiting: { background: '#eff3fe', border: '1px solid #bfdbfe' },   // Step5AskCard ASK_BOX
  note: { background: '#fff', border: '1px solid #e5e7eb', color: '#000' },
  doneTick: '#16a34a',
  overdue: '#b91c1c',
  primary: { background: '#3a6ad9', color: '#fff' },
});

/** The circle and the due label of a checklist item. overdue = past due and not done. */
export function checklistItemColours({ done = false, overdue = false } = {}) {
  if (done) return { circleBg: CHECK_COLOURS.doneTick, circleBorder: CHECK_COLOURS.doneTick, tick: '#fff', label: null };
  if (overdue) return { circleBg: '#fff', circleBorder: CHECK_COLOURS.overdue, tick: null, label: CHECK_COLOURS.overdue };
  return { circleBg: '#fff', circleBorder: '#d1d5db', tick: null, label: '#374151' };
}

/** The index of the one item waiting on the borrower: the first not done, not optional, that they can act on, else -1. */
export function waitingIndex(items) {
  return (items || []).findIndex((it) => it && it.kind !== 'notice' && !it.done && !it.optional && it.actionable !== false);
}

/** Is a 'YYYY-MM-DD' due date past (the day after it has begun)? */
export function isOverdue(ymd, now = new Date()) {
  const p = ymdParts(ymd);
  if (!p) return false;
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Date.UTC(p.y, p.mo - 1, p.d) < today;
}

// ── 🔑 THE QUICK ACTION CARDS, BACK BELOW THE MAIN CARD (Hannah's production review, 2026-11-03 item 2) ──
// Restored from the pre-redesign dashboard (git fa57ab66^ pages/Dashboard.js "Quick Actions"), same destinations, same
// stage rule, restyled as the page's white cards:
//   View Documents   → /documents (view only — the label says so; uploads live on the guided steps)
//   SmartEstimate    → the simulator (SmartEstimatePreview in the dashboard's modal) — NEVER the signed PDF; the hero's
//                      "View your SmartEstimate" is the signed PDF
//   Browse Homes     → /homes; the Homes Locked dialog while the nav's Homes is locked (constants/navBadges homesLocked —
//                      the old card used an allow-list that locked under_contract / offer_accepted borrowers out)
//   What's Next      → the What's Next dialog (its stage checklist, unchanged)
// From option_period, closing and closed the middle two are Schedule (/schedule) and View Alerts (/alerts), as before.
// 🔑 The simulator card's wording: APPROVED by Hannah 2026-11-03 ("SmartEstimate Simulator" / "See how a different
// purchase price or down payment amount changes your numbers") — replaces "SmartEstimate" / "See how your loan works".
export const QUICK_ACTION_SCHEDULE_STAGES = ['option_period', 'closing', 'closed'];

export function quickActionsFor(stage) {
  const docs = { key: 'documents', to: '/documents', label: 'View Documents', sub: 'Manage your required files' };
  const next = { key: 'whats-next', action: 'whats-next', label: 'What\'s Next', sub: 'See your next steps' };
  if (QUICK_ACTION_SCHEDULE_STAGES.includes(String(stage || ''))) {
    return [docs,
      { key: 'schedule', to: '/schedule', label: 'Schedule', sub: 'View your key dates' },
      { key: 'alerts', to: '/alerts', label: 'View Alerts', sub: 'Check your latest updates' },
      next];
  }
  const simulator = { key: 'smartestimate', action: 'simulator', label: 'SmartEstimate Simulator',
    sub: 'See how a different purchase price or down payment amount changes your numbers' };
  return [docs, simulator, { key: 'browse', action: 'browse', to: '/homes', label: 'Browse Homes', sub: 'View available listings' }, next];
}

// ── 🔑 THE FIVE STEP NAMES UNDER THE BAR (Hannah's production review, 2026-11-03 item 4) — desktop and tablet (sm and
// up); the current one bold. A phone keeps the bar and "Step X of 5" (JOURNEY_COPY.stepLine, already approved).
export function journeyStepNameRow(step) {
  return JOURNEY_STEP_NAMES.map((name, i) => ({ name, step: i + 1, current: i + 1 === Number(step) }));
}

// ── 🔑 THE DASHBOARD GREETING — OPTIONS FOR HANNAH (2026-11-03), ONE CONSTANT ──
// 'current' (shipped): the small "Welcome back, [first name]" line above "Step X of 5". The others are built for her
// pick (.scratch/reports/dashboard-greeting); choosing one is this one line. The step label, the step title and the bar
// stay as approved in every option.
//   'a' — no line above; the guided flow's slim white welcome card (components/guided/GuidedWelcomeCard) BELOW the bar:
//         "Welcome back, [first name]."
//   'b' — one small line above the step title: "Welcome back, [first name] · Step X of 5" (the separate "Step X of 5"
//         label is not shown, so it is never said twice).
//   'c' — no greeting line; the first name leads the main card's headline: "[First name], you’re approved up to $X"
//         (each stage's real headline, its first letter lower-cased after the name).
//   'd' — the step label row carries the greeting at its right ("Step X of 5" left, "Welcome back, [first name]" right):
//         no extra line, the step title stays the heading.
// No first name → "Welcome back" (a: "Welcome back."); c: the headline unchanged.
export const DASHBOARD_GREETING = 'current';
export const DASHBOARD_GREETING_OPTIONS = ['current', 'a', 'b', 'c', 'd'];

/** The main card's headline with the first name in front (option 'c'); unchanged without a name. */
export function headlineWithName(first, headline) {
  const h = String(headline || '');
  if (!first || !h) return h;
  return `${first}, ${h.charAt(0).toLowerCase()}${h.slice(1)}`;
}
